import { motion } from 'motion/react'
import React, { useState } from 'react'
import styled from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import { Separator } from '@root/shared/ui-kit'

import { TradingPairMetricsPanelSummary } from './trading-pair-metrics-panel-summary'
import { TradingPairMetricsTableTab } from './trading-pair-metrics-table-tabs'

import type { MetricsTheme } from './constants'
import type { PairMetrics } from '@root/entities/pair'
import type { PairModel } from '@root/entities/pair/atoms/reatom-pair'

type TradingPairMetricsProps = {
  expandable?: boolean
  metricsTheme: MetricsTheme
  pair: PairModel | undefined
}

const spans: Array<PairMetrics.MetricsSpan> = ['5M', '1H', '6H', '1D']

export const TradingPairMetricsTable: React.FC<TradingPairMetricsProps> = ({
  expandable = true,
  metricsTheme,
  pair,
}) => {
  const haptics = useHapticFeedback()
  const [activeInterval, setActiveInterval] = useState<
    PairMetrics.MetricsSpan | undefined
  >(expandable ? undefined : '5M')

  const renderTab = (span: PairMetrics.MetricsSpan, isLast: boolean) => {
    const stats = pair?.stats[span]

    return (
      <TradingPairMetricsTableTab
        isLast={isLast}
        stats={stats}
        metricsTheme={metricsTheme}
        expanded={activeInterval !== undefined}
        active={span === activeInterval}
        interval={span}
        onClick={(intervale) => {
          haptics.impactOccurred('light')
          const value =
            expandable && intervale === activeInterval ? undefined : intervale

          setActiveInterval(value)
        }}
      />
    )
  }

  return (
    <Root>
      <TabsContainer>
        {spans.map((span, idx) => {
          const active = span === activeInterval
          const activeNext = spans.at(idx + 1) === activeInterval
          const isLast = spans.length - 1 === idx

          return (
            <React.Fragment key={span}>
              {renderTab(span, isLast)}
              {<TabSeparator vertical $active={active || activeNext} />}
            </React.Fragment>
          )
        })}
      </TabsContainer>
      {activeInterval ? (
        <Content initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <TradingPairMetricsPanelSummary
            metrics={pair?.stats[activeInterval]}
            metricsTheme={metricsTheme}
          />
        </Content>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 6px;
`

const TabsContainer = styled.header`
  border: 1px solid var(--background-2);
  display: grid;
  grid-template-columns:
    minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr)
    auto minmax(0, 1fr);

  border-radius: 12px;
  padding: 2px;
  gap: 1px;
`

const TabSeparator = styled(Separator)<{ $active: boolean }>`
  opacity: ${({ $active }) => ($active ? 0 : 1)};
`

const Content = styled(motion.div)``
